import { useSubmit } from "@certn/api";
import {
  BasicButton,
  ButtonGroup,
  DangerButton,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Modal,
  PrimaryButton,
  showToast,
} from "@certn/cascadia-react";
import React from "react";
import { Trans, useTranslation } from "react-i18next";

type GroupActiveToggleModalProps = {
  groupId: string;
  onClose: () => void;
  onSuccess: () => Promise<void>;
  groupName: string;
  isActive: boolean;
};

export function GroupActiveToggleModal({
  onClose,
  onSuccess,
  groupId,
  groupName,
  isActive,
}: GroupActiveToggleModalProps) {
  const { t } = useTranslation();
  const endpoint = isActive ? "deactivate" : "activate";
  const toggle = isActive ? "deactivate" : "reactivate";
  const { submit } = useSubmit(
    `/api/internal/client-portal/customer-groups/${groupId}/${endpoint}/`,
    {
      showToastOnError: true,
      onSuccess: async () => {
        await onSuccess();
        showToast(
          t(`Success. The group {{groupName}} has been {{toggle}}d.`, {
            groupName,
            toggle,
          }),
          {
            type: "success",
          }
        );
      },
    }
  );

  const toggleText = isActive ? t("Deactivate group") : t("Reactivate group");
  const ToggleButton = isActive ? DangerButton : PrimaryButton;
  return (
    <Modal isOpen>
      <Dialog>
        <DialogHeader onClose={onClose}>
          {`${toggleText}: ${groupName}`}
        </DialogHeader>
        <DialogContent>
          {isActive
            ? t(
                `By clicking "{{toggle}} group", all users will be removed from the group and it will no longer be accessible in the Client Portal.`,
                {
                  toggle: toggle?.charAt(0).toUpperCase() + toggle?.slice(1),
                }
              )
            : "Are you sure you want to reactivate this group?"}
        </DialogContent>
        <DialogFooter>
          <ButtonGroup>
            <BasicButton size="medium" onPress={onClose} testID="cancel-button">
              <Trans>Cancel</Trans>
            </BasicButton>
            <ToggleButton
              size="medium"
              onPress={() => submit()}
              testID={
                isActive ? "deactivate-group-button" : "reactivate-group-button"
              }
            >
              {toggleText}
            </ToggleButton>
          </ButtonGroup>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}
